import { useMemo, useState, memo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import _get from 'lodash/get';
import {
  FilterGroupingEnum,
  SiteArea,
  useSectorsSearchListLazyQuery,
  useSitesSearchListLazyQuery,
} from 'graphql-common';
import ModalTableMultiselect, {
  VisibleViaQrColumnConfig,
  useModalTableAllTabManager,
} from '@lib/components/ModalTableMultiselect/ModalTableMultiselect';
import ModalTableMultiunselect from '@lib/components/ModalTableMultiselect/ModalTableMultiunselect';
import { FormRefType } from '@lib/components/ReactHookForm/types';
import useModalTableModeManager from '@lib/components/ModalTableMultiselect/hooks/useModalTableModeManager';
import useSelectedFormFieldIds from '@lib/components/ModalTableMultiselect/hooks/useSelectedFormFieldIds';
import useVisibleLinkableIds from '@lib/components/ModalTableMultiselect/hooks/useVisibleLinkableIds';
import { ModalTableMultiselectMode } from '@lib/components/ModalTableMultiselect/enums';
import { getSitesLoadOptions as getSitesLoadOptionsFn } from 'utils/fetch/searchLists/sitesSearchList';
import { getSectorsLoadOptions as getSectorsLoadOptionsFn } from 'utils/fetch/searchLists/sectorsSearchList';
import useSiteAreasQueryHook from 'utils/fetch/hooks/useSiteAreasQueryHook';
import { useDocumentationLinksByDocumentation } from 'utils/fetch/hooks/useDocumentationLinksMap';
import { Values } from '@lib/interfaces/form';
import { FieldNames } from 'routes/Task/enums';
import { GetLoadOptions } from '@lib/components/Select/types';
import { getLoadOptionsWithSelectedFilter } from '@lib/components/SelectPanel/utils/getLoadOptionsWithSelectedFilter';
import useFilterFields, {
  FieldNames as FilterFieldNames,
} from './utils/getFilterFields';
import getSiteAreasColumns from './utils/getSiteAreasColumns';
import { createAdditionalFilters } from './utils/createAdditionalFilters';

export { ModalTableMultiselectMode } from '@lib/components/ModalTableMultiselect/enums';

type SiteAreasModalTableMultiselectProps = {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (values: string[], visibilityMap?: Record<string, boolean>) => void;
  formRef: FormRefType;
  fieldName?: string;
  mode?: ModalTableMultiselectMode;
  /**
   * Documentation id this picker is editing for. When provided, the picker
   * lazily fetches the documentation↔area visibility baseline on `isOpen` and
   * uses it to seed the "Visible via QR" column for currently-linked rows.
   */
  documentationId?: string;
  /**
   * "Visible via QR" column wiring. See AssetsModalTableMultiselect for shape
   * parity.
   */
  visibleViaQr?: {
    initialVisibilityMap?: Record<string, boolean>;
    onVisibilityMapChange?: (next: Record<string, boolean>) => void;
  };
};

function SiteAreasModalTableMultiselect({
  isOpen,
  onClose,
  onSelect,
  formRef,
  fieldName = FieldNames.siteArea,
  mode = ModalTableMultiselectMode.WithTabs,
  documentationId,
  visibleViaQr,
}: SiteAreasModalTableMultiselectProps) {
  const { t } = useTranslation();
  const [isFetchingAllIds, setIsFetchingAllIds] = useState(false);

  const selectedSiteAreas = useSelectedFormFieldIds(formRef, fieldName);

  // Important: keep a stable reference to avoid re-running effects in hooks
  const filterStateDefault = useMemo(
    () => ({
      [FilterFieldNames.Sectors]: [],
      [FilterFieldNames.Sites]: [],
    }),
    [],
  );

  const {
    activeTab,
    activeTabState,
    clearFilter,
    filterState,
    filterStateSelected,
    idsForSelectedTabQuery,
    isSelectedOnlyMode,
    isSelectedTabActive,
    orderingState,
    orderingStateSelected,
    paginationState,
    paginationStateSelected,
    searchState,
    searchStateSelected,
    setSelectedIdsForQuery,
  } = useModalTableModeManager({
    isOpen,
    mode,
    selectedIds: selectedSiteAreas,
    filterStateDefault,
  });
  const { shouldRunAllQuery, handleAllTabQueryResult, getAllTabSnapshot } =
    useModalTableAllTabManager<SiteArea>({
      isOpen,
      mode,
      activeTab,
      isSelectedOnlyMode,
    });

  const searchQueryFilterSchema = [
    'name',
    'code',
    'siteName',
    'siteCode',
    'sectorName',
  ];

  const additionalFilters = useMemo(() => {
    if (isSelectedOnlyMode || !filterState) return undefined;
    const filterWithSites = {
      ...filterState[0],
      [FilterFieldNames.Sites]:
        ((filterState[0] &&
          filterState[0][FilterFieldNames.Sites]) as Values[]) || [],
    };
    return createAdditionalFilters(filterWithSites);
  }, [filterState, isSelectedOnlyMode]);

  // All tab query - only fetch when All tab is active and in WithTabs mode
  const {
    collection,
    loading: siteAreasLoading,
    firstLoading: siteAreasFirstLoading,
    totalCount,
    fetchIds,
  } = useSiteAreasQueryHook({
    searchQuery: searchState?.[0] || '',
    searchQueryFilterSchema,
    paginationParams: {
      ...(orderingState?.[0] || {}),
      ...(paginationState?.[0] || {}),
    },
    additionalFilters,
    skip: !shouldRunAllQuery,
    fetchPolicy: 'cache-first',
    includeSiteData: true,
  });

  useEffect(() => {
    handleAllTabQueryResult({
      collection,
      totalCount,
      loading: siteAreasLoading,
      firstLoading: siteAreasFirstLoading,
    });
  }, [
    collection,
    handleAllTabQueryResult,
    siteAreasFirstLoading,
    siteAreasLoading,
    totalCount,
  ]);

  const {
    collection: allTabCollection,
    totalCount: allTabTotalCount,
    firstLoading: allTabFirstLoading,
  } = getAllTabSnapshot({
    collection,
    totalCount,
    firstLoading: siteAreasFirstLoading,
  });

  const additionalFiltersSelected = useMemo(() => {
    const filterWithSites = {
      ...(filterStateSelected[0] || {}),
      [FilterFieldNames.Sites]:
        ((filterStateSelected[0] &&
          filterStateSelected[0][FilterFieldNames.Sites]) as Values[]) || [],
    };
    return createAdditionalFilters(filterWithSites);
  }, [filterStateSelected]);

  // Query for Selected tab - only fetch when Selected tab is active and has IDs
  const {
    collection: collectionSelectedRaw,
    loading: siteAreasLoadingSelected,
    firstLoading: siteAreasFirstLoadingSelected,
    totalCount: totalCountSelectedRaw,
  } = useSiteAreasQueryHook({
    searchQuery: searchStateSelected[0],
    searchQueryFilterSchema,
    paginationParams: {
      ...orderingStateSelected[0],
      ...paginationStateSelected[0],
    },
    additionalFilters: isSelectedOnlyMode
      ? undefined
      : additionalFiltersSelected,
    ids: idsForSelectedTabQuery,
    skip:
      !isOpen || !isSelectedTabActive || idsForSelectedTabQuery.length === 0,
    fetchPolicy: 'cache-first',
    includeSiteData: true,
  });

  // Return empty data when no IDs are selected to avoid showing stale cached data
  const collectionSelected = useMemo(
    () => (idsForSelectedTabQuery.length === 0 ? [] : collectionSelectedRaw),
    [idsForSelectedTabQuery, collectionSelectedRaw],
  );
  const totalCountSelected =
    idsForSelectedTabQuery.length === 0 ? 0 : totalCountSelectedRaw;

  // Sectors query
  const [fetchSectorsSearchListLazyQuery] = useSectorsSearchListLazyQuery({
    fetchPolicy: 'cache-and-network',
  });
  const getSectorsLoadOptions = getLoadOptionsWithSelectedFilter((filters) =>
    getSectorsLoadOptionsFn(fetchSectorsSearchListLazyQuery, filters),
  );

  // Sites query
  const [fetchSitesSearchListLazyQuery] = useSitesSearchListLazyQuery({
    fetchPolicy: 'cache-and-network',
  });
  const getSitesLoadOptions: GetLoadOptions = getLoadOptionsWithSelectedFilter(
    (filters) =>
      getSitesLoadOptionsFn(fetchSitesSearchListLazyQuery, filters, {
        includeSector: true,
      }),
    (values) => {
      // Get sectors filter from values
      const sectors = _get(values, FilterFieldNames.Sectors) || [];
      const sectorIds = sectors.map(({ value }) => value) as string[];

      // Build filters array
      const filters: Array<Record<string, unknown>> = [];
      // Add sector filter if sectors are selected
      if (sectorIds?.length) {
        filters.push({
          sectorId: {
            grouping: FilterGroupingEnum.And,
            predicate: { in: sectorIds },
            filterGroup: 'sectorId',
          },
        });
      }
      return filters;
    },
  );

  const columns = getSiteAreasColumns({ t });

  const visibleAreaIds = useVisibleLinkableIds(
    allTabCollection,
    collectionSelected,
  );

  const { byAreaId: visibilityByAreaId, loading: visibilityLoading } =
    useDocumentationLinksByDocumentation({
      documentationId,
      visibleLinkableIds: visibleAreaIds,
      skip: !isOpen || !documentationId || !visibleViaQr,
    });

  const visibleViaQrColumnConfig:
    | VisibleViaQrColumnConfig<SiteArea>
    | undefined = visibleViaQr
    ? {
        enabled: true,
        defaultValue: true,
        initialVisibilityMap: visibleViaQr.initialVisibilityMap,
        onVisibilityMapChange: visibleViaQr.onVisibilityMapChange,
        getInitialValue: (item) => visibilityByAreaId[String(item.id)],
      }
    : undefined;
  const filterFormFields = useFilterFields({
    getSitesLoadOptions,
    getSectorsLoadOptions,
    clearFilter,
  });

  // Render ModalTableMultiunselect for SelectedOnly mode
  if (isSelectedOnlyMode) {
    return (
      <ModalTableMultiunselect<SiteArea>
        caption={t('details-summary')}
        columns={columns}
        dataSelected={collectionSelected}
        firstLoadingSelected={siteAreasFirstLoadingSelected}
        isOpen={isOpen}
        loadingSelected={siteAreasLoadingSelected || isFetchingAllIds}
        onClose={onClose}
        onSelect={onSelect}
        onSelectedIdsChange={setSelectedIdsForQuery}
        orderingStateSelected={orderingStateSelected}
        paginationStateSelected={paginationStateSelected}
        searchPlaceholder={t('tasks-areas-search-placeholder')}
        searchStateSelected={searchStateSelected}
        selected={selectedSiteAreas}
        totalEntriesSelected={totalCountSelected}
      />
    );
  }

  // Render ModalTableMultiselect for WithTabs mode
  // These values should always be defined in WithTabs mode
  if (
    !activeTabState ||
    !searchState ||
    !paginationState ||
    !orderingState ||
    !filterState
  ) {
    return null;
  }

  return (
    <ModalTableMultiselect<SiteArea>
      activeTabState={activeTabState}
      caption={t('area-multiselect')}
      columns={columns}
      data={allTabCollection}
      dataSelected={collectionSelected}
      fetchIds={fetchIds}
      filterFormFields={filterFormFields}
      filterState={filterState}
      filterStateSelected={filterStateSelected}
      firstLoading={allTabFirstLoading}
      firstLoadingSelected={siteAreasFirstLoadingSelected}
      isOpen={isOpen}
      loading={
        (shouldRunAllQuery ? siteAreasLoading : false) || visibilityLoading
      }
      loadingSelected={
        siteAreasLoadingSelected || isFetchingAllIds || visibilityLoading
      }
      onClose={onClose}
      onIsFetchingAllIdsChange={setIsFetchingAllIds}
      onSelect={(values, _sites, visibilityMap) =>
        onSelect(values, visibilityMap)
      }
      onSelectedIdsChange={setSelectedIdsForQuery}
      orderingState={orderingState}
      orderingStateSelected={orderingStateSelected}
      paginationState={paginationState}
      paginationStateSelected={paginationStateSelected}
      searchPleceholder={t('tasks-areas-search-placeholder')}
      searchState={searchState}
      searchStateSelected={searchStateSelected}
      selected={selectedSiteAreas}
      totalEntries={allTabTotalCount}
      totalEntriesSelected={totalCountSelected}
      visibleViaQrColumnConfig={visibleViaQrColumnConfig}
    />
  );
}

export default memo(SiteAreasModalTableMultiselect);
